import React from "react";
import {
  Container,
  Divider,
  Grid,
  Typography,
  Box,
  Chip,
  Stack,
} from "@mui/material";
import { Link } from "react-router-dom";

// Icons
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import FacebookIcon from "@mui/icons-material/Facebook";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import InstagramIcon from "@mui/icons-material/Instagram";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import SpaOutlinedIcon from "@mui/icons-material/SpaOutlined";
import PaymentOutlinedIcon from "@mui/icons-material/PaymentOutlined";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";

const AppFooter = () => {
  const trustPillars = [
    {
      icon: <SpaOutlinedIcon sx={{ fontSize: 22, color: "#10b981" }} />,
      title: "100% Ayurvedic",
      desc: "Pure & natural herbal extracts",
    },
    {
      icon: <VerifiedOutlinedIcon sx={{ fontSize: 22, color: "#10b981" }} />,
      title: "GMP & ISO Certified",
      desc: "Highest manufacturing standards",
    },
    {
      icon: <LocalShippingOutlinedIcon sx={{ fontSize: 22, color: "#10b981" }} />,
      title: "Free Express Shipping",
      desc: "On all orders above ₹499",
    },
    {
      icon: <LockOutlinedIcon sx={{ fontSize: 22, color: "#10b981" }} />,
      title: "Razorpay Secured",
      desc: "256-bit encrypted checkout",
    },
  ];

  const categoryLinks = [
    { text: "Herbal Hair Care", link: "/collections" },
    { text: "Shilajit & Vitality", link: "/collections" },
    { text: "Men's Health & Boosters", link: "/collections" },
    { text: "Immunity & Daily Wellness", link: "/collections" },
    { text: "All Ayurvedic Products", link: "/collections" },
  ];

  const quickLinks = [
    { text: "Track Your Order", link: "/userOrders" },
    { text: "My Wishlist", link: "/myWishlist" },
    { text: "Shopping Cart", link: "/cart" },
    { text: "About Inproveda", link: "/about-inproveda" },
    { text: "Customer Support", link: "/contactUs" },
  ];

  const policyLinks = [
    { text: "Privacy Policy", link: "/privacy-policy" },
    { text: "Refund & Returns", link: "/refund-policy" },
    { text: "Shipping Policy", link: "/shipping-policy" },
    { text: "Terms & Conditions", link: "/terms-and-conditions" },
  ];

  const socialLinks = [
    {
      icon: <FacebookIcon sx={{ fontSize: 26 }} />,
      href: "https://www.facebook.com/profile.php?id=61559031269996",
      hoverColor: "#1877f2",
      label: "Facebook",
    },
    {
      icon: <InstagramIcon sx={{ fontSize: 26 }} />,
      href: "https://www.instagram.com/inprovedahealthcare/",
      hoverColor: "#e4405f",
      label: "Instagram",
    },
    {
      icon: <LinkedInIcon sx={{ fontSize: 26 }} />,
      href: "https://www.linkedin.com/in/inprovedahealthcare-products-b36a6037/",
      hoverColor: "#0077b5",
      label: "LinkedIn",
    },
    {
      icon: <WhatsAppIcon sx={{ fontSize: 26 }} />,
      href: "https://api.whatsapp.com/send/?phone=919411174186",
      hoverColor: "#25d366",
      label: "WhatsApp",
    },
  ];

  const paymentBadges = [
    "UPI", "Google Pay", "PhonePe", "Paytm",
    "Visa", "Mastercard", "RuPay", "Net Banking", "COD",
  ];

  // Reusable nav link style
  const navLinkSx = {
    color: "#94a3b8",
    textDecoration: "none",
    fontSize: "0.85rem",
    lineHeight: 1.6,
    display: "flex",
    alignItems: "center",
    gap: 0.5,
    transition: "all 0.2s ease",
    "&:hover": {
      color: "#10b981",
      gap: 1,
    },
  };

  // Reusable column heading style
  const colHeadingSx = {
    fontWeight: 700,
    color: "#ffffff",
    fontSize: "0.9rem",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    mb: 2.5,
    pb: 1.25,
    borderBottom: "2px solid #10b981",
    display: "inline-block",
  };

  return (
    <Box
      component="footer"
      sx={{
        background: "linear-gradient(160deg, #062e27 0%, #041f1a 60%, #020f0d 100%)",
        color: "#ffffff",
        mt: { xs: 5, md: 8 },
        position: "relative",
        borderTop: "2px solid rgba(16, 185, 129, 0.2)",
        pb: { xs: "85px", sm: "70px", md: "35px" },
      }}
    >
      {/* ── 1. Trust Pillars Strip ── */}
      <Box
        sx={{
          borderBottom: "1px solid rgba(255, 255, 255, 0.06)",
          background: "rgba(0,0,0,0.25)",
          py: { xs: 2.5, md: 3 },
        }}
      >
        <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          <Grid container spacing={{ xs: 2, md: 0 }}>
            {trustPillars.map((item, index) => (
              <Grid item xs={6} md={3} key={index}>
                <Stack
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  sx={{
                    px: { md: 3 },
                    py: { xs: 0.5, md: 0 },
                    borderRight: {
                      md: index < trustPillars.length - 1
                        ? "1px solid rgba(255,255,255,0.07)"
                        : "none",
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      bgcolor: "rgba(16, 185, 129, 0.12)",
                      border: "1px solid rgba(16, 185, 129, 0.3)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 700,
                        color: "#f1f5f9",
                        fontSize: { xs: "0.8rem", md: "0.88rem" },
                        lineHeight: 1.2,
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#64748b",
                        display: "block",
                        fontSize: "0.72rem",
                        mt: 0.25,
                      }}
                    >
                      {item.desc}
                    </Typography>
                  </Box>
                </Stack>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── 2. Main Content Grid ── */}
      <Container
        maxWidth="xl"
        sx={{ px: { xs: 2, sm: 3, md: 4 }, pt: { xs: 5, md: 7 }, pb: { xs: 4, md: 5 } }}
      >
        <Grid container spacing={{ xs: 4, md: 5 }}>

          {/* Column 1: Brand */}
          <Grid item xs={12} sm={12} md={4}>
            {/* Brand name with leaf accent */}
            <Box sx={{ mb: 2.5 }}>
              <Link to="/" style={{ textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 8 }}>
                <Box
                  sx={{
                    width: 6,
                    height: 32,
                    bgcolor: "#10b981",
                    borderRadius: "3px",
                    flexShrink: 0,
                  }}
                />
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 800,
                    color: "#ffffff",
                    letterSpacing: "-0.02em",
                  }}
                >
                  Inproveda
                </Typography>
              </Link>
            </Box>

            <Typography
              variant="body2"
              sx={{
                color: "#64748b",
                lineHeight: 1.75,
                mb: 3,
                fontSize: "0.85rem",
                maxWidth: 340,
              }}
            >
              Pure Ayurvedic healing rooted in the spiritual wellness capital of Haridwar.
              Authentic, time-tested herbal healthcare crafted for modern lifestyles.
            </Typography>

            {/* Contact Info */}
            <Stack spacing={1.75}>
              <Stack direction="row" spacing={1.25} alignItems="flex-start">
                <LocationOnOutlinedIcon
                  sx={{ fontSize: 16, color: "#10b981", mt: 0.2, flexShrink: 0 }}
                />
                <Typography
                  variant="caption"
                  sx={{ color: "#64748b", lineHeight: 1.6, fontSize: "0.78rem" }}
                >
                  276, Bhagirathi Vihar, Haridwar, Uttarakhand — 249408
                </Typography>
              </Stack>

              <Stack direction="row" spacing={1.25} alignItems="center">
                <PhoneOutlinedIcon sx={{ fontSize: 16, color: "#10b981", flexShrink: 0 }} />
                <Typography
                  component="a"
                  href="tel:+919411174186"
                  variant="caption"
                  sx={{
                    color: "#94a3b8",
                    textDecoration: "none",
                    fontSize: "0.82rem",
                    fontWeight: 600,
                    transition: "color 0.2s ease",
                    "&:hover": { color: "#10b981" },
                  }}
                >
                  +91-9411174186
                </Typography>
              </Stack>

              <Stack direction="row" spacing={1.25} alignItems="center">
                <EmailOutlinedIcon sx={{ fontSize: 16, color: "#10b981", flexShrink: 0 }} />
                <Typography
                  component="a"
                  href="mailto:inproveda@gmail.com"
                  variant="caption"
                  sx={{
                    color: "#94a3b8",
                    textDecoration: "none",
                    fontSize: "0.82rem",
                    transition: "color 0.2s ease",
                    "&:hover": { color: "#10b981" },
                  }}
                >
                  inproveda@gmail.com
                </Typography>
              </Stack>
            </Stack>
          </Grid>

          {/* Column 2: Herbal Categories */}
          <Grid item xs={6} sm={4} md={2.5}>
            <Typography sx={colHeadingSx}>Shop</Typography>
            <Stack spacing={1.5}>
              {categoryLinks.map((item, index) => (
                <Typography
                  key={index}
                  component={Link}
                  to={item.link}
                  sx={navLinkSx}
                >
                  <ChevronRightRoundedIcon sx={{ fontSize: 14, color: "#10b981", flexShrink: 0 }} />
                  {item.text}
                </Typography>
              ))}
            </Stack>
          </Grid>

          {/* Column 3: Customer Care */}
          <Grid item xs={6} sm={4} md={2.5}>
            <Typography sx={colHeadingSx}>Support</Typography>
            <Stack spacing={1.5}>
              {quickLinks.map((item, index) => (
                <Typography
                  key={index}
                  component={Link}
                  to={item.link}
                  sx={navLinkSx}
                >
                  <ChevronRightRoundedIcon sx={{ fontSize: 14, color: "#10b981", flexShrink: 0 }} />
                  {item.text}
                </Typography>
              ))}
            </Stack>
          </Grid>

          {/* Column 4: Policies + Follow Us */}
          <Grid item xs={12} sm={4} md={3}>
            <Typography sx={colHeadingSx}>Policies</Typography>
            <Stack spacing={1.5}>
              {policyLinks.map((item, index) => (
                <Typography
                  key={index}
                  component={Link}
                  to={item.link}
                  sx={navLinkSx}
                >
                  <ChevronRightRoundedIcon sx={{ fontSize: 14, color: "#10b981", flexShrink: 0 }} />
                  {item.text}
                </Typography>
              ))}
            </Stack>

            {/* Follow Us */}
            <Box sx={{ mt: 3.5 }}>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  color: "#475569",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  mb: 1.25,
                }}
              >
                Follow Us
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  flexWrap: "nowrap",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                {socialLinks.map((social, index) => (
                  <Box
                    key={index}
                    component="a"
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.label}
                    title={social.label}
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "fit-content",
                      height: "fit-content",
                      lineHeight: 0,
                      color: "#475569",
                      transition: "color 0.2s ease, transform 0.2s ease",
                      "&:hover": {
                        color: social.hoverColor,
                        transform: "translateY(-2px)",
                      },
                    }}
                  >
                    {social.icon}
                  </Box>
                ))}
              </Box>
            </Box>
          </Grid>
        </Grid>

        {/* ── 3. Payment Badges Bar ── */}
        <Box
          sx={{
            mt: { xs: 5, md: 6 },
            pt: 3,
            borderTop: "1px solid rgba(255, 255, 255, 0.06)",
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: { xs: "center", md: "space-between" },
            gap: 2,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <PaymentOutlinedIcon sx={{ fontSize: 16, color: "#10b981" }} />
            <Typography variant="caption" sx={{ color: "#475569", fontWeight: 600, fontSize: "0.75rem" }}>
              Secure Payment Options
            </Typography>
          </Stack>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, justifyContent: { xs: "center", md: "flex-end" } }}>
            {paymentBadges.map((badge, idx) => (
              <Chip
                key={idx}
                label={badge}
                size="small"
                sx={{
                  bgcolor: "rgba(255, 255, 255, 0.04)",
                  color: "#64748b",
                  border: "1px solid rgba(255, 255, 255, 0.07)",
                  fontSize: "0.68rem",
                  fontWeight: 600,
                  height: 22,
                  borderRadius: "4px",
                }}
              />
            ))}
          </Box>
        </Box>

        {/* ── 4. Copyright Bar ── */}
        <Divider sx={{ my: 3, borderColor: "rgba(255, 255, 255, 0.05)" }} />
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1.5,
            textAlign: { xs: "center", sm: "left" },
          }}
        >
          <Typography variant="caption" sx={{ color: "#334155", fontSize: "0.75rem" }}>
            © {new Date().getFullYear()} Inproveda Healthcare. All rights reserved.
          </Typography>

          <Typography variant="caption" sx={{ color: "#334155", fontSize: "0.75rem" }}>
            Developed by{" "}
            <Box
              component="a"
              href="https://softaim.in"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: "#0ea5e9",
                textDecoration: "none",
                fontWeight: 600,
                transition: "color 0.2s ease",
                "&:hover": { color: "#38bdf8" },
              }}
            >
              Softaim Technologies Pvt. Ltd.
            </Box>
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default AppFooter;
